// BOM lists and order files (packages/shared domain/bom.ts): the models' BOMs as uploaded from the yard's STD
// lists workbook, the rules that bring an option section into a boat's BOM, each model's standard equipment,
// and the boats' order files (Info / Files › New order file). A boat's choices are its Order Sheet row, so
// saving an order file refreshes the Order Sheet too. Each model's sheet of the STD lists workbook is kept as
// uploaded, so the BOM a boat gets is that sheet with the sections it hasn't got deleted (features/stdSheet.ts).
// The worker routes are in apps/worker/src/routes/bom.ts.
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BomConfig,
  BomLinkSetInput,
  BomList,
  BomListMeta,
  BomStdSetInput,
  BomUploadInput,
  OrderFileRecord,
  OrderFileSaveInput,
} from '@technohull/shared';
import { api } from '../lib/api.js';

type Overview = { lists: BomListMeta[]; config: BomConfig };

/** every uploaded list (without parts), the rules people set and the standard equipment */
export function useBom(enabled = true) {
  return useQuery({ queryKey: ['bom'], queryFn: () => api.get<Overview>('/api/bom'), enabled, staleTime: 60_000 });
}

const listQuery = (season: string, model: string) => ({
  queryKey: ['bom', 'list', season, model],
  queryFn: () => api.get<{ list: BomList }>(`/api/bom/list?season=${encodeURIComponent(season)}&model=${encodeURIComponent(model)}`).then((r) => r.list),
  staleTime: 5 * 60_000,
});

/** one model's list for a season, with its parts */
export function useBomList(season: string, model: string, enabled = true) {
  return useQuery({ ...listQuery(season, model), enabled: enabled && Boolean(season && model) });
}

/** several lists at once (the Parts totals over many boats) */
export function useBomLists(pairs: ReadonlyArray<{ season: string; model: string }>) {
  return useQueries({ queries: pairs.map((p) => listQuery(p.season, p.model)) });
}

/** the list a boat's BOM comes from: its season's, else the latest uploaded before it */
export function bomSeasonFor(lists: readonly BomListMeta[], model: string, season: string): string {
  const mine = lists.filter((l) => l.model === model).map((l) => l.season).sort().reverse();
  return mine.find((s) => s <= season) ?? mine[mine.length - 1] ?? '';
}

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const sheetPath = (season: string, model: string) => `/api/bom/sheet?season=${encodeURIComponent(season)}&model=${encodeURIComponent(model)}`;

/** save the season's lists, then keep each model's sheet of the workbook they came from, as it is; the result
 *  says which models' sheets couldn't be kept (their lists are saved all the same) */
export function useUploadBom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: BomUploadInput & { workbook: File; tabs: Readonly<Record<string, string>> }) => {
      const { workbook, tabs, ...input } = v;
      const res = await api.post<{ ok: true; count: number }>('/api/bom/lists', input);
      const { stdSheetWorkbook } = await import('./stdSheet.js');
      const source = new Uint8Array(await workbook.arrayBuffer());
      const missed: string[] = [];
      await Promise.all(input.lists.map(async (l) => {
        try {
          const sheet = stdSheetWorkbook(source, { sheet: tabs[l.model] ?? l.sheet });
          await api.upload(sheetPath(input.season, l.model), sheet, XLSX_TYPE);
        } catch {
          missed.push(l.model);
        }
      }));
      return { ...res, missed };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bom'] }),
  });
}

/** the sheet's name in the workbook, from a list's sheet ("GT7 BOXES · UPD 8/10/26" → "GT7 BOXES") */
export const sheetTab = (sheet: string) => sheet.replace(/\s·\sUPD\s.*$/, '');

/** a model's sheet of the season's STD lists workbook, as it was uploaded */
export function fetchBomSheet(season: string, model: string): Promise<Uint8Array<ArrayBuffer>> {
  return api.bytes(sheetPath(season, model));
}

export function useDeleteBomList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { season: string; model: string }) => api.post('/api/bom/lists/delete', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bom'] }),
  });
}

/** set a section's rule (null puts it back on its suggestion), shown at once */
export function useSetBomLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: BomLinkSetInput) => api.post('/api/bom/link', v),
    onMutate: (v) => {
      qc.setQueryData<Overview>(['bom'], (d) => {
        if (!d) return d;
        const model = { ...(d.config.links[v.model] ?? {}) };
        if (v.link) model[v.sectionKey] = v.link; else delete model[v.sectionKey];
        return { ...d, config: { ...d.config, links: { ...d.config.links, [v.model]: model } } };
      });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['bom'], exact: true }),
  });
}

export function useSetStdEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: BomStdSetInput) => api.post('/api/bom/std', v),
    onMutate: (v) => {
      qc.setQueryData<Overview>(['bom'], (d) => {
        if (!d) return d;
        const std = { ...d.config.std };
        for (const i of v.items) std[i.model] = i.fields;
        return { ...d, config: { ...d.config, std } };
      });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['bom'], exact: true }),
  });
}

/** the season's order files */
export function useOrderFiles(season: string, enabled = true) {
  return useQuery({
    queryKey: ['order-files', season],
    queryFn: () => api.get<{ files: OrderFileRecord[] }>(`/api/order-files?season=${encodeURIComponent(season)}`).then((r) => r.files),
    enabled: enabled && Boolean(season),
    staleTime: 30_000,
  });
}

/** save a boat's order file; its choices land on the boat's Order Sheet row */
export function useSaveOrderFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: OrderFileSaveInput) => api.post<{ ok: true; file: OrderFileRecord }>('/api/order-files', v).then((r) => r.file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['order-files'] });
      void qc.invalidateQueries({ queryKey: ['order-sheet'] });
    },
  });
}

/** put the boat's workbook in Info / Files and note where it went, with the files that go with it (its BOM) */
export function useStoreOrderWorkbook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { sap: string; prefix: string; file: File; with?: readonly File[] }) => {
      const put = (f: File) => api.upload<{ ok: true; key: string }>(
        `/api/files/upload?prefix=${encodeURIComponent(v.prefix)}&name=${encodeURIComponent(f.name)}`,
        f,
        f.type || 'application/octet-stream',
      );
      const up = await put(v.file);
      await api.post('/api/order-files/file', { sap: v.sap, key: up.key });
      for (const f of v.with ?? []) await put(f);
      return up.key;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['files'] });
      void qc.invalidateQueries({ queryKey: ['order-files'] });
    },
  });
}
